import React from 'react';
import {Product} from '@/_application/modules/products/model/product';
import {ProductListItemsQueryOptions} from '@/_application/modules/products/request/product-list.request';
import {ApolloClient, ApolloError, NetworkStatus, NormalizedCacheObject} from '@apollo/client';
import {
    AdditionalDataAttribute,
    Aggregation,
    ProductImage,
    ProductVideo,
    SearchResultPageInfo,
    SortFields,
} from '__generated__/__types__';
import {useRouter} from 'next/router';

import {
    InterestedProductsQuery,
    ProductListItemsQuery,
    useAdditionalDataAttributeProductQuery,
    useCrossSellProductBySkuQuery,
    useInterestedProductsQuery,
    useNotificationsProductBySkuLazyQuery,
    useProductLazyQuery,
    useProductListAggregationsLazyQuery,
    useProductListAggregationsQuery,
    useProductListByKeyLazyQuery,
    useProductListItemsLazyQuery,
    useProductListSortFieldsLazyQuery,
    useProductListSortFieldsQuery,
    useProductMediaGalleryBySkuQuery,
    useRelatedProductsOfProductQuery,
    useUserGuideProductBySkuQuery,
    useProductBadgesBySkuQuery,
} from '@/framework/gql/products.graphql';

export function useProduct(): {
    product?: Product;
    isLoading: boolean;
    error?: ApolloError;
    // eslint-disable-next-line no-unused-vars
    getProductBySku: (sku: string) => Promise<Product>;
} {
    const [fetchProductLazy, {data, loading: isLoading, error}] = useProductLazyQuery();

    const getProductBySku = async (sku: string) => {
        const response = await fetchProductLazy({
            variables: {
                sku: sku,
            },
        });
        return response?.data?.products?.items?.at(0) as Product;
    };
    return {
        getProductBySku,
        product: (data?.products?.items?.at(0) as Product) ?? undefined,
        isLoading,
        error,
    };
}

export function useRelatedProductsOfProduct({sku}: {sku: string}): {
    products: Product[];
    isLoading: boolean;
    error?: ApolloError;
} {
    const {
        data,
        loading: isLoading,
        error,
    } = useRelatedProductsOfProductQuery({
        variables: {
            sku: sku,
        },
    });
    return {
        products: (data?.products?.items ?? []) as Product[],
        isLoading,
        error,
    };
}

export function useProductMediaGallery({sku}: {sku: string}) {
    const {data, loading: isLoading, error} = useProductMediaGalleryBySkuQuery({variables: {sku: sku}});

    let gallery: (ProductImage | ProductVideo)[] = [];

    if (
        data?.productBySku?.__typename === 'ConfigurableProduct' ||
        data?.productBySku?.__typename === 'SimpleProduct'
    ) {
        gallery = data?.productBySku?.media_gallery as (ProductImage | ProductVideo)[];
    }

    return {
        gallery: gallery,
        isLoading,
        error,
    };
}

// Re-export from server-safe module for backward compatibility
export {getProductListItems} from './product-list.server';

export function useProductListItems(options: Partial<ProductListItemsQueryOptions>): {
    products: ProductListItemsQuery['products'];
    isLoading: boolean;
    error?: ApolloError;
    isLoadingMore: boolean;
    loadMore: () => void;
    hasMore: boolean;
    pageInfo?: SearchResultPageInfo;
    totalCount: number;
    fetchData: (page?: number) => Promise<ProductListItemsQuery['products']>;
    fetchDataFromPages: Function;
} {
    const {filter} = options;
    const {locale} = useRouter();
    const localeOptions = {
        language: locale,
        ...options,
        ...{
            filter: {
                ...filter,
                ...(filter?.price && {
                    price: {
                        ...filter.price,
                        ...{to: `${+(filter?.price?.to ?? 0)}`}, // add step interval for price is 100k by config - KIDSBUGS-72
                    },
                }),
            },
        },
    };

    const [fetchProductListItemsQuery, {data, loading: isLoading, error, fetchMore, networkStatus}] =
        useProductListItemsLazyQuery({
            notifyOnNetworkStatusChange: true,
        });

    function handleLoadMore() {
        if (data?.products?.page_info?.current_page! < data?.products?.page_info?.total_pages!) {
            fetchMore({
                variables: {
                    currentPage: data?.products?.page_info?.current_page! + 1,
                },
                updateQuery: (prev, {fetchMoreResult}) => {
                    if (!fetchMoreResult) return prev;
                    return fetchMoreResult;
                },
            });
        }
    }

    const fetchData = async (page?: number) => {
        const response = await fetchProductListItemsQuery({
            variables: {
                ...localeOptions,
                ...(page && {currentPage: page}),
            },
        });
        return response.data?.products;
    };

    const fetchDataFromPages = async () => {
        for (let startPage = 1; startPage <= (localeOptions.currentPage ?? 1); startPage++) {
            if (startPage === 1) {
                await fetchProductListItemsQuery({
                    variables: {
                        ...localeOptions,
                        currentPage: startPage,
                    },
                });
            } else {
                await fetchMore({
                    variables: {
                        ...localeOptions,
                        currentPage: startPage,
                    },
                    updateQuery: (prev, {fetchMoreResult}) => {
                        if (!fetchMoreResult) return {};
                        return fetchMoreResult;
                    },
                });
            }
        }
    };

    return {
        fetchData,
        fetchDataFromPages,
        products: data?.products ?? {items: [], total_count: 0, page_info: {current_page: 1, total_pages: 1}},
        isLoading,
        error,
        isLoadingMore: networkStatus === NetworkStatus.fetchMore,
        loadMore: handleLoadMore,
        hasMore: Boolean(data?.products?.page_info?.current_page! < data?.products?.page_info?.total_pages!),
        pageInfo: data?.products?.page_info ?? undefined,
        totalCount: data?.products?.total_count ?? 0,
    };
}

export function useProductListItemsLazy(options: Partial<ProductListItemsQueryOptions>): {
    products: ProductListItemsQuery['products'];
    isLoading: boolean;
    error?: ApolloError;
    isLoadingMore: boolean;
    hasMore: boolean;
    pageInfo?: SearchResultPageInfo;
    totalCount: number;
    executeProductListItem: () => Promise<any>;
} {
    const {locale} = useRouter();

    const localeOptions = {
        language: locale,
        ...options,
    };

    const [executeProductListItem, {data, loading: isLoading, error, networkStatus}] = useProductListItemsLazyQuery({
        variables: localeOptions,
        notifyOnNetworkStatusChange: true,
    });

    return {
        products: data?.products,
        isLoading,
        error,
        isLoadingMore: networkStatus === NetworkStatus.fetchMore,
        hasMore: Boolean(data?.products?.page_info?.current_page! < data?.products?.page_info?.total_pages!),
        pageInfo: data?.products?.page_info ?? undefined,
        totalCount: data?.products?.total_count ?? 0,
        executeProductListItem,
    };
}

export function useInterestedProducts(): {
    data: InterestedProductsQuery['interestedProducts'];
    isLoading: boolean;
    error?: ApolloError;
} {
    const {data, loading: isLoading, error} = useInterestedProductsQuery();

    return {
        data: data?.interestedProducts,
        isLoading,
        error,
    };
}

/**
 * Get category aggregations by category id
 * @param categoryId
 */
export function useCategoryAggregations(categoryId: number): {
    data: Aggregation[];
    isLoading: boolean;
    error?: ApolloError;
} {
    const {
        data,
        loading: isLoading,
        error,
    } = useProductListAggregationsQuery({
        variables: {
            filter: {
                category_id: {
                    eq: `${categoryId}`,
                },
            },
        },
    });
    return {
        data: (data?.products?.aggregations ?? []) as Aggregation[],
        isLoading,
        error,
    };
}

/**
 * Get category sorti fields by category id
 * @param categoryId
 */
export function useCategorySortFields(categoryId: number): {
    data: SortFields;
    isLoading: boolean;
    error?: ApolloError;
} {
    const {
        data,
        loading: isLoading,
        error,
    } = useProductListSortFieldsQuery({
        variables: {
            filter: {
                category_id: {
                    eq: `${categoryId}`,
                },
            },
        },
    });
    return {
        data: (data?.products?.sort_fields ?? []) as SortFields,
        isLoading,
        error,
    };
}

export function useProductListBySkuList() {
    const [fetchProductListByKeyQuery, {data, loading: isLoading, error}] = useProductListByKeyLazyQuery({});

    const fetchData = async (skuList: string[]) => {
        const response = await fetchProductListByKeyQuery({
            variables: {
                filter: {
                    sku: {
                        in: skuList,
                    },
                },
            },
        });

        return response?.data?.products;
    };

    return {
        data: (data?.products?.items as Product[]) ?? [],
        fetchData,
        isLoading,
        error,
    };
}

/**
 * Get category aggregations by filter options
 */
export function useProductsAggregations() {
    const {locale} = useRouter();

    const [fetchAggregationQuery, {data, loading: isLoading, error}] = useProductListAggregationsLazyQuery();

    const fetchAggregation = (options: Partial<ProductListItemsQueryOptions>) => {
        const localeOptions = {
            language: locale,
            ...options,
        };
        return fetchAggregationQuery({
            variables: localeOptions,
        });
    };
    return {
        fetchAggregation,
        data: (data?.products?.aggregations ?? []) as Aggregation[],
        isLoading,
        error,
    };
}

/**
 * Get category sorted fields by filter options
 */
export function useProductSortFields() {
    const {locale} = useRouter();
    const [fetchSortFieldsQuery, {data, loading: isLoading, error}] = useProductListSortFieldsLazyQuery();

    const fetchSortFields = (options: Partial<ProductListItemsQueryOptions>) => {
        const localeOptions = {
            language: locale,
            ...options,
        };
        return fetchSortFieldsQuery({
            variables: localeOptions,
        });
    };
    return {
        fetchSortFields,
        data: (data?.products?.sort_fields ?? []) as SortFields,
        isLoading,
        error,
    };
}

export function useCrossSellProductBySku({sku}: {sku: string}): {
    crossSellProducts: Product[];
    isLoading: boolean;
    error?: ApolloError;
} {
    const {
        data,
        loading: isLoading,
        error,
    } = useCrossSellProductBySkuQuery({
        variables: {
            sku: sku,
        },
    });

    return {
        crossSellProducts: (data?.products?.items?.[0] as any)?.crosssell_products,
        isLoading,
        error,
    };
}

export function useAdditionalDataAttributeProductBySku({sku}: {sku: string}) {
    const {data, loading: isLoading, error} = useAdditionalDataAttributeProductQuery({variables: {sku: sku}});

    let additionalDataAttribute: AdditionalDataAttribute[] = [];

    if (
        data?.productBySku?.__typename === 'SimpleProduct' ||
        data?.productBySku?.__typename === 'ConfigurableProduct'
    ) {
        additionalDataAttribute = data.productBySku.additional_data_attributes as AdditionalDataAttribute[];
    }

    return {
        additionalDataAttribute,
        isLoading,
        error,
    };
}

export function useNotificationsProductBySku() {
    const [getNotificationsProductQuery, {data, loading: isLoading, error}] = useNotificationsProductBySkuLazyQuery();

    const getNotificationsProduct = React.useCallback(
        (sku: string) => {
            return getNotificationsProductQuery({variables: {sku}});
        },
        [getNotificationsProductQuery],
    );

    return {
        notifications: (data?.products?.items?.[0] as any)?.notifications?.[0],
        isLoading,
        error,
        getNotificationsProduct,
    };
}

export const useUserGuideProductBySku = ({sku}: {sku: string}) => {
    const {data, loading, error} = useUserGuideProductBySkuQuery({variables: {sku: sku}});

    return {
        userGuide: data?.productBySku,
        isLoading: loading,
        error,
    };
};

export const useProductBadgesBySku = ({sku}: {sku: string}) => {
    const {data, loading, error} = useProductBadgesBySkuQuery({variables: {sku: sku}});

    return {
        badges: data?.productBySku?.product_badges ?? [],
        isLoading: loading,
        error,
    };
};
